/**
 * ConflictToast - displays notifications when remote changes overwrite
 * local optimistic updates (last-write-wins conflict resolution).
 *
 * Each toast auto-dismisses after 6 seconds. Users can also dismiss manually.
 * Toasts stack vertically from the top-right on desktop, top-center on mobile.
 */

import { useEffect } from 'react';
import { useConflictsStore, type ConflictNotice } from '../store/conflicts';

const AUTO_DISMISS_MS = 6000;

function entityLabel(type: string): string {
  switch (type) {
    case 'event': return 'Event';
    case 'task': return 'Task';
    case 'note': return 'Note';
    default: return 'Item';
  }
}

function ToastItem({ notice }: { notice: ConflictNotice }) {
  const dismiss = useConflictsStore((s) => s.dismissConflict);

  useEffect(() => {
    const timer = setTimeout(() => dismiss(notice.id), AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [notice.id, dismiss]);

  return (
    <div
      className="flex items-start gap-3 px-4 py-3 rounded-lg shadow-lg border max-w-sm animate-slide-in"
      style={{
        backgroundColor: 'var(--bg-secondary, #1e1e2e)',
        borderColor: 'var(--accent, #6366f1)',
        color: 'var(--text-primary, #e0e0e0)',
      }}
      role="alert"
    >
      <div className="flex-shrink-0 mt-0.5">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">
          {entityLabel(notice.entityType)} updated
        </p>
        <p className="text-xs mt-0.5 opacity-80 truncate">
          "{notice.entityTitle}" was updated from another device. The latest version is now shown.
        </p>
      </div>
      <button
        onClick={() => dismiss(notice.id)}
        className="flex-shrink-0 ml-2 opacity-60 hover:opacity-100 transition-opacity"
        aria-label="Dismiss"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
}

export function ConflictToast() {
  const conflicts = useConflictsStore((s) => s.conflicts);

  if (conflicts.length === 0) return null;

  return (
    <div
      className="fixed top-2 right-2 z-50 flex flex-col gap-2 md:top-4 md:right-4"
      style={{ paddingTop: 'var(--sat, 0px)' }}
    >
      {conflicts.map((c) => (
        <ToastItem key={c.id} notice={c} />
      ))}
    </div>
  );
}
